import { useState, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
  maxWidth?: string;
}

export function Tooltip({
  children,
  content,
  side = "top",
  className,
  maxWidth = "max-w-xs",
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), 200);
  };

  const handleLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const tooltipEl = tooltipRef.current;
      const tooltipWidth = tooltipEl?.offsetWidth || 0;
      const tooltipHeight = tooltipEl?.offsetHeight || 0;

      let top = 0;
      let left = 0;

      switch (side) {
        case "top":
          top = rect.top - tooltipHeight - 8 + window.scrollY;
          left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
          break;
        case "bottom":
          top = rect.bottom + 8 + window.scrollY;
          left = rect.left + rect.width / 2 - tooltipWidth / 2 + window.scrollX;
          break;
        case "left":
          top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
          left = rect.left - tooltipWidth - 8 + window.scrollX;
          break;
        case "right":
          top = rect.top + rect.height / 2 - tooltipHeight / 2 + window.scrollY;
          left = rect.right + 8 + window.scrollX;
          break;
      }

      // Clamp to viewport
      const padding = 8;
      left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding + window.scrollX));
      top = Math.max(padding + window.scrollY, top);

      setPosition({ top, left });
    };

    // Position on next frame so tooltip dimensions are available
    requestAnimationFrame(updatePosition);
  }, [open, side]);

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            className={cn(
              "fixed z-[9999] px-3 py-2 text-sm rounded-lg shadow-lg border border-border bg-white text-foreground",
              "animate-in fade-in-0 zoom-in-95 duration-150",
              maxWidth,
              className
            )}
            style={{ top: position.top, left: position.left, position: "absolute" }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
}
